import { useState } from "react";
import { Link } from "react-router-dom";
import { Shield, Mail, ArrowLeft } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { toast } from "sonner";
import axios from "axios";
import { API } from "../App";
import { useI18n } from "../i18n/I18nProvider";
import { extractError } from "../lib/errors";

const ForgotPasswordPage = () => {
  const { t, locale } = useI18n();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, {
        email: email.trim().toLowerCase(),
        locale: locale === "es" ? "es" : "en",
      });
      // Siempre mostramos el mismo mensaje — anti-enumeración
      setSubmitted(true);
    } catch (err) {
      // Si falla (rate-limit, red, etc.) mostramos el error pero NO revelamos
      // si el email existe o no.
      if (err?.response?.status === 429) {
        toast.error(t("auth.forgot.rate_limit") || "Too many attempts. Try again later.");
      } else {
        toast.error(extractError(err, t("auth.forgot.generic_response")));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="dark min-h-screen flex items-center justify-center bg-[#0a0a0a] p-6">
      <div className="w-full max-w-md">
        <Link to="/auth">
          <Button variant="ghost" className="mb-6">
            <ArrowLeft className="w-4 h-4 mr-2" />
            {t("auth.forgot.back_to_login")}
          </Button>
        </Link>

        <Card className="border-border shadow-lg">
          <CardHeader className="text-center pb-2">
            <div className="w-12 h-12 rounded-xl bg-primary mx-auto flex items-center justify-center mb-4">
              <Shield className="w-7 h-7 text-primary-foreground" />
            </div>
            <CardTitle className="text-2xl font-heading">
              {t("auth.forgot.title")}
            </CardTitle>
            {!submitted && (
              <CardDescription>
                {locale === "es"
                  ? "Ingresa tu correo y te enviaremos un enlace para restablecer tu contraseña."
                  : "Enter your email and we'll send you a link to reset your password."}
              </CardDescription>
            )}
          </CardHeader>

          <CardContent>
            {submitted ? (
              <div className="space-y-4 text-center">
                <div className="w-12 h-12 rounded-full bg-primary/10 mx-auto flex items-center justify-center">
                  <Mail className="w-6 h-6 text-primary" />
                </div>
                <p className="text-sm text-muted-foreground">
                  {t("auth.forgot.generic_response")}
                </p>
                <Link to="/auth">
                  <Button variant="outline" className="w-full mt-2">
                    {t("auth.forgot.back_to_login")}
                  </Button>
                </Link>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="forgot-email">{t("auth.forgot.email_label")}</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      id="forgot-email"
                      type="email"
                      placeholder="you@company.com"
                      className="pl-10"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      autoComplete="email"
                      data-testid="forgot-email-input"
                    />
                  </div>
                </div>
                <Button
                  type="submit"
                  className="w-full"
                  disabled={loading}
                  data-testid="forgot-submit"
                >
                  {loading
                    ? locale === "es" ? "Enviando..." : "Sending..."
                    : t("auth.forgot.submit")}
                </Button>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
